/**
 * ContactPage — self-contained lead capture form
 *
 * Submits to POST /api/contact (FastAPI backend).
 * Backend saves the lead locally and sends an email notification via SMTP.
 * No external service required — works from any network / social media referral.
 *
 * If VITE_ZOHO_CONTACT_FORM_URL is set, the Zoho Forms embed (forms.zoho.in)
 * is shown instead (opt-in when Zoho India account is configured).
 */

import { useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import './ZohoContactForm.css';

const ZOHO_FORM_URL = import.meta.env.VITE_ZOHO_CONTACT_FORM_URL as string | undefined;

const INTERESTS = [
  'General Enquiry',
  'Sanskrit Learning',
  'Dhanurveda Programme',
  'Tutor on Demand',
  'Career Opportunity',
  'Volunteer / Collaborate',
  'Contractor / Partnership',
  'Media / Press',
  'Other',
];

type Status = 'idle' | 'sending' | 'success' | 'error';

export default function ZohoContactForm() {
  const { t } = useGlossary();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', interest: INTERESTS[0], message: '',
  });
  const [status, setStatus]   = useState<Status>('idle');
  const [errors, setErrors]   = useState<Record<string, string>>({});
  const [leadId, setLeadId]   = useState('');

  function update(field: string, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim())    e.name    = 'नाम आवश्यक है।';
    if (!form.email.trim() || !form.email.includes('@'))
                               e.email   = 'वैध ईमेल आवश्यक है।';
    if (form.message.trim().length < 10)
                               e.message = 'संदेश कम से कम 10 अक्षर का हो।';
    return e;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus('sending');
    try {
      const referrer = document.referrer || undefined;
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, referrer }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLeadId(data.lead_id ?? '');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  // ── Optional: Zoho Forms embed (when configured) ──────────────────────────
  if (ZOHO_FORM_URL) {
    return (
      <div className="zoho-contact-wrapper">
        <h1 className="zoho-contact-title">📞 {t('contact.title')}</h1>
        <p className="zoho-contact-subtitle">{t('contact.subtitle')}</p>
        <div className="zoho-form-embed-container">
          <p className="zoho-form-notice">
            🔒 यह फ़ॉर्म Zoho India (<code>forms.zoho.in</code>) द्वारा संचालित है।
            आपका डेटा भारत में ही संग्रहित किया जाएगा।{' '}
            <a href="https://www.zoho.in/privacy.html" target="_blank" rel="noopener noreferrer">{t('contact.privacyLink')}</a>
          </p>
          <iframe
            title={t('contact.zoho.title')}
            src={ZOHO_FORM_URL}
            width="100%" height="620"
            className="zoho-form-iframe"
            frameBorder={0}
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  // ── Success state ──────────────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div className="zoho-contact-wrapper">
        <div className="contact-success">
          <div className="contact-success-icon">✅</div>
          <h2>{t('contact.success.title')}</h2>
          <p>
            <strong>{form.name}</strong>{t('contact.success.body', { email: form.email })}
          </p>
          {leadId && (
            <p className="contact-ref">{t('contact.success.reference')}: <code>{leadId}</code></p>
          )}
          <button className="contact-btn" onClick={() => {
            setStatus('idle');
            setForm({ name: '', email: '', phone: '', interest: INTERESTS[0], message: '' });
          }}>
            {t('contact.back')}
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  return (
    <div className="zoho-contact-wrapper">
      <h1 className="zoho-contact-title">📞 {t('contact.title')}</h1>
      <p className="zoho-contact-subtitle">{t('contact.subtitle')}</p>

      <div className="contact-grid">
        {/* ── Form ── */}
        <form className="contact-form" onSubmit={handleSubmit} noValidate>

          <div className="contact-field">
            <label>{t('contact.field.name')} <span className="req">*</span></label>
            <input
              type="text" value={form.name} autoComplete="name"
              placeholder={t('contact.placeholder.name')}
              onChange={e => update('name', e.target.value)}
            />
            {errors.name && <div className="contact-err">{errors.name}</div>}
          </div>

          <div className="contact-field">
            <label>{t('contact.field.email')} <span className="req">*</span></label>
            <input
              type="email" value={form.email} autoComplete="email"
              placeholder={t('contact.placeholder.email')}
              onChange={e => update('email', e.target.value)}
            />
            {errors.email && <div className="contact-err">{errors.email}</div>}
          </div>

          <div className="contact-field">
            <label>{t('contact.field.phone')} <span className="opt">({t('contact.optional')})</span></label>
            <input
              type="tel" value={form.phone} autoComplete="tel"
              placeholder={t('contact.placeholder.phone')}
              onChange={e => update('phone', e.target.value)}
            />
          </div>

          <div className="contact-field">
            <label>{t('contact.field.interest')}</label>
            <select value={form.interest} onChange={e => update('interest', e.target.value)}>
              {INTERESTS.map(i => <option key={i} value={i}>{t(`contact.interest.${i}`)}</option>)}
            </select>
          </div>

          <div className="contact-field">
            <label>{t('contact.field.message')} <span className="req">*</span></label>
            <textarea
              rows={5} value={form.message}
              placeholder={t('contact.placeholder.message')}
              onChange={e => update('message', e.target.value)}
            />
            {errors.message && <div className="contact-err">{errors.message}</div>}
          </div>

          {status === 'error' && (
            <div className="contact-err contact-err-global">
              {t('contact.error')}
            </div>
          )}

          <button
            type="submit"
            className="contact-btn"
            disabled={status === 'sending'}
          >
            {status === 'sending' ? t('contact.sending') : t('contact.submit')}
          </button>

          <p className="contact-privacy-note">
            {t('contact.privacy')}
          </p>
        </form>

        {/* ── Sidebar ── */}
        <div className="contact-sidebar">
          <div className="contact-info-card">
            <h3>📧 {t('contact.sidebar.email')}</h3>
            <ul>
              <li><a href="mailto:contact@raajadharma.org">contact@raajadharma.org</a></li>
              <li><a href="mailto:career@raajadharma.org">career@raajadharma.org</a></li>
              <li><a href="mailto:dhanurveda@raajadharma.org">dhanurveda@raajadharma.org</a></li>
              <li><a href="mailto:sanskrit@raajadharma.org">sanskrit@raajadharma.org</a></li>
            </ul>
          </div>
          <div className="contact-info-card">
            <h3>🏛️ {t('contact.sidebar.centers')}</h3>
            <ul>
              <li>{t('contact.center.dharamshala')}</li>
              <li>{t('contact.center.delhi')}</li>
              <li>{t('contact.center.ranchi')}</li>
              <li>{t('contact.center.jaynagar')}</li>
              <li>{t('contact.center.burnaby')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
